<template>
  <div class="request-form-view">
    <div class="form-container">
      <div class="form-header">
        <h1>Request Equipment</h1>
        <p>Fill out this form to request computer equipment for your student</p>
      </div>

      <div v-if="successMessage" class="success-message">
        {{ successMessage }}
      </div>

      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>

      <form v-if="!successMessage" @submit.prevent="submitRequest" class="request-form">
        <!-- Student Information -->
        <section class="form-section">
          <h2>Student Information</h2>
          <div class="form-row">
            <div class="form-group">
              <label for="student_name">Child's Name *</label>
              <input
                id="student_name"
                v-model="formData.student_name"
                type="text"
                required
                placeholder="Enter student's full name"
              >
            </div>
            <div class="form-group">
              <label for="grade_level">Grade Level *</label>
              <select id="grade_level" v-model="formData.grade_level" required>
                <option value="">Select grade level...</option>
                <option value="Pre-K">Pre-K</option>
                <option value="K">K</option>
                <option value="1st">1st</option>
                <option value="2nd">2nd</option>
                <option value="3rd">3rd</option>
                <option value="4th">4th</option>
                <option value="5th">5th</option>
                <option value="6th">6th</option>
                <option value="7th">7th</option>
                <option value="8th">8th</option>
                <option value="9th">9th</option>
                <option value="10th">10th</option>
                <option value="11th">11th</option>
                <option value="12th">12th</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="school">School *</label>
              <select
                id="school"
                v-model="formData.school"
                required
                @change="onSchoolChange"
              >
                <option value="">-- Select School --</option>
                <option v-for="school in schools" :key="school.id" :value="school.id">
                  {{ school.name }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="teacher_select">Teacher</label>
              <select
                id="teacher_select"
                v-model="selectedTeacher"
                :disabled="!formData.school || loadingTeachers"
                @change="onTeacherSelect"
              >
                <option value="">{{ loadingTeachers ? 'Loading teachers...' : '-- Select Teacher or Enter Email Below --' }}</option>
                <option v-for="teacher in teachers" :key="teacher.id" :value="teacher.id">
                  {{ teacher.first_name.charAt(0) }}. {{ teacher.last_name }}
                </option>
                <option value="other">Other (enter email below)</option>
              </select>
              <input
                v-if="showEmailInput"
                id="teacher_email"
                v-model="formData.teacher_email"
                type="email"
                placeholder="teacher@school.edu"
                class="teacher-email-input"
              >
            </div>
          </div>

          <div class="form-group">
            <label for="technology_access">Current Access to Technology at Home *</label>
            <textarea
              id="technology_access"
              v-model="formData.technology_access"
              rows="3"
              required
              placeholder="e.g., Shared family computer, tablet, smartphone only..."
            ></textarea>
          </div>
        </section>

        <!-- Contact Information -->
        <section class="form-section">
          <h2>Contact Information</h2>
          <div class="form-row">
            <div class="form-group">
              <label for="contact_email">Email *</label>
              <input
                id="contact_email"
                v-model="formData.contact_email"
                type="email"
                required
                placeholder="your-email@example.com"
              >
            </div>
            <div class="form-group">
              <label for="contact_phone">Phone</label>
              <input
                id="contact_phone"
                v-model="formData.contact_phone"
                type="tel"
                placeholder="(321) 555-1234"
              >
            </div>
          </div>
        </section>


        <!-- Task Requirements -->
        <section class="form-section">
          <h2>What will the student use this equipment for? *</h2>
          <p class="section-description">Select all tasks that apply</p>

          <div v-if="loadingTasks" class="loading">Loading tasks...</div>
          <div v-else>
            <div v-for="(tasks, category) in taskGroups" :key="category" class="task-category">
              <h3>{{ category }}</h3>
              <div class="task-checkboxes">
                <label v-for="task in tasks" :key="task.id" class="checkbox-label">
                  <input
                    type="checkbox"
                    :value="task.id"
                    v-model="formData.needed_for"
                  >
                  <div class="task-info">
                    <span class="task-label">{{ task.label }}</span>
                    <span class="task-description">{{ task.description }}</span>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </section>

        <!-- Software Requirements -->
        <!-- <section class="form-section">
          <h2>Required Software</h2>
          <div class="form-group">
            <label for="needed_software">List any specific software applications needed</label>
            <textarea
              id="needed_software"
              v-model="softwareText"
              rows="4"
              placeholder="e.g., Microsoft Office, Adobe Photoshop, Visual Studio Code, Zoom&#10;Enter one per line or separated by commas"
            ></textarea>
            <small>Enter software names separated by commas or one per line</small>
          </div>
        </section> -->

        <!-- Additional Comments -->
        <section class="form-section">
          <h2>Additional Information</h2>
          <div class="form-group">
            <label for="additional_comments">Additional Comments</label>
            <textarea
              id="additional_comments"
              v-model="formData.additional_comments"
              rows="4"
              placeholder="Any additional information you'd like us to know..."
            ></textarea>
          </div>
        </section>

        <!-- Submit Button -->
        <div class="form-actions">
          <button type="submit" :disabled="submitting || formData.needed_for.length === 0" class="btn-submit">
            {{ submitting ? 'Submitting...' : 'Submit Request' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable no-undef */
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import { useSchools } from '@/composables/useSchools'

const { schools, loadSchools } = useSchools()

interface Task {
  id: string
  label: string
  description: string
}

interface TaskGroups {
  [category: string]: Task[]
}

interface Teacher {
  id: string
  first_name: string
  last_name: string
  email: string
}

const formData = ref({
  student_name: '',
  grade_level: '',
  school: '',
  teacher_email: '',
  technology_access: '',
  contact_email: '',
  contact_phone: '',
  equipment_type: 'laptop',
  needed_for: [] as string[],
  needed_software: [] as string[],
  additional_comments: ''
})

const teachers = ref<Teacher[]>([])
const selectedTeacher = ref('')
const loadingTeachers = ref(false)
const showEmailInput = ref(true)
const softwareText = ref('')
const loadingTasks = ref(true)
const taskGroups = ref<TaskGroups>({})
const submitting = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

// Parse software text into array
const parsedSoftware = computed(() => {
  if (!softwareText.value.trim()) return []

  // Split by newlines or commas
  return softwareText.value
    .split(/[\n,]+/)
    .map(s => s.trim())
    .filter(s => s.length > 0)
})

// Load available tasks from API
const loadTasks = async () => {
  try {
    const response = await axios.get('/api/v1/requests/tasks/available')
    taskGroups.value = response.data.groups
  } catch (error) {
    console.error('Error loading tasks:', error)
    errorMessage.value = 'Failed to load task options. Please refresh the page.'
  } finally {
    loadingTasks.value = false
  }
}

// Load teachers for selected school
const loadTeachers = async (schoolId: string) => {
  if (!schoolId) {
    teachers.value = []
    return
  }

  loadingTeachers.value = true
  try {
    const response = await axios.get(`/api/v1/schools/${schoolId}/teachers`)
    teachers.value = response.data
  } catch (error) {
    console.error('Error loading teachers:', error)
    teachers.value = []
  } finally {
    loadingTeachers.value = false
  }
}

// Handle school change
const onSchoolChange = () => {
  selectedTeacher.value = ''
  formData.value.teacher_email = ''
  showEmailInput.value = true
  if (formData.value.school) {
    loadTeachers(formData.value.school)
  } else {
    teachers.value = []
  }
}

// Handle teacher selection
const onTeacherSelect = () => {
  if (selectedTeacher.value && selectedTeacher.value !== 'other') {
    // Find the selected teacher and set their email
    const teacher = teachers.value.find(t => t.id === selectedTeacher.value)
    if (teacher) {
      formData.value.teacher_email = teacher.email
      showEmailInput.value = false
    }
  } else if (selectedTeacher.value === 'other') {
    // Show email input and clear the email field
    formData.value.teacher_email = ''
    showEmailInput.value = true
  } else {
    // No teacher selected
    formData.value.teacher_email = ''
    showEmailInput.value = true
  }
}

const submitRequest = async () => {
  if (formData.value.needed_for.length === 0) {
    errorMessage.value = 'Please select at least one task the student needs the equipment for.'
    return
  }

  submitting.value = true
  errorMessage.value = ''
  successMessage.value = ''

  try {
    // Parse software list
    formData.value.needed_software = parsedSoftware.value

    const response = await axios.post('/api/v1/requests', formData.value)

    successMessage.value = 'Request submitted successfully! We will review your request and contact you soon.'

    // Reset form
    formData.value = {
      student_name: '',
      grade_level: '',
      school: '',
      teacher_email: '',
      technology_access: '',
      contact_email: '',
      contact_phone: '',
      equipment_type: 'laptop',
      needed_for: [],
      needed_software: [],
      additional_comments: ''
    }
    softwareText.value = ''

    // Scroll to top to show success message
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } catch (error: any) {
    console.error('Error submitting request:', error)
    errorMessage.value = error.response?.data?.detail || 'Failed to submit request. Please try again.'
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  loadTasks()
  loadSchools()
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@600;700&family=Inter:wght@400;500;600&display=swap');

.request-form-view {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 2rem 1rem;
}

.form-container {
  max-width: 900px;
  margin: 0 auto;
  background: var(--white, #ffffff);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.form-header {
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  padding: 2rem;
  text-align: center;
  color: var(--white, #ffffff);
}

.form-header h1 {
  margin: 0 0 0.5rem 0;
  font-family: 'Poppins', sans-serif;
  font-size: 2rem;
}

.form-header p {
  margin: 0;
  opacity: 0.95;
  font-family: 'Inter', sans-serif;
}

.success-message {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: 1.5rem 2rem;
  margin: 2rem;
  border-radius: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 1.1rem;
  line-height: 1.6;
}

.error-message {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: 1rem 1.5rem;
  margin: 1.5rem 1.5rem 0 1.5rem;
  border-radius: 6px;
  font-family: 'Inter', sans-serif;
}

.request-form {
  padding: 2rem;
}

.form-section {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid #e0e0e0;
}

.form-section:last-of-type {
  border-bottom: none;
}

.form-section h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 1.3rem;
  color: var(--primary-color, #2c4a6b);
  margin: 0 0 0.5rem 0;
}

.section-description {
  color: var(--text-light, #666666);
  margin: 0 0 1rem 0;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--text-dark, #333333);
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 1rem;
  font-family: 'Inter', sans-serif;
  transition: border-color 0.3s;
  background: var(--white, #ffffff);
  color: var(--text-dark, #333333);
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--secondary-color, #3ba3d1);
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
}

.form-group small {
  margin-top: 0.25rem;
  color: var(--text-light, #666666);
  font-size: 0.85rem;
}

.teacher-email-input {
  margin-top: 0.5rem;
}

.loading {
  text-align: center;
  padding: 2rem;
  color: var(--text-light, #666666);
  font-family: 'Inter', sans-serif;
}

.task-category {
  margin-bottom: 1.5rem;
}

.task-category h3 {
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: var(--primary-color, #2c4a6b);
  margin: 0 0 0.75rem 0;
  font-weight: 600;
}

.task-checkboxes {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 0.75rem;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  font-family: 'Inter', sans-serif;
  border: 1px solid transparent;
}

.checkbox-label:hover {
  background: var(--light-bg, #f5f7fa);
  border-color: #e0e0e0;
}

.task-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.task-label {
  font-weight: 600;
  color: var(--text-dark, #333333);
  font-size: 0.95rem;
}

.task-description {
  font-size: 0.85rem;
  color: var(--text-light, #666666);
  line-height: 1.4;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  border: 2px solid var(--secondary-color, #3ba3d1);
  border-radius: 3px;
  background: var(--white, #ffffff);
  position: relative;
  margin: 0;
}

.checkbox-label input[type="checkbox"]:checked {
  background: var(--secondary-color, #3ba3d1);
  border-color: var(--secondary-color, #3ba3d1);
}

.checkbox-label input[type="checkbox"]:checked::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 14px;
  font-weight: bold;
}

.form-actions {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
}

.btn-submit {
  padding: 1rem 3rem;
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  color: var(--white, #ffffff);
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s;
  font-family: 'Inter', sans-serif;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(59, 163, 209, 0.4);
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .request-form-view {
    padding: 1rem 0.5rem;
  }

  .form-header h1 {
    font-size: 1.5rem;
  }

  .request-form {
    padding: 1.5rem 1rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .task-checkboxes {
    grid-template-columns: 1fr;
  }

  .btn-submit {
    width: 100%;
  }
}
</style>
